import { AuthContext, githubStorageService } from "@harvestr-api/common";
import { logger, parallelPromiseHelper } from "@harvestr-api/utils";
import crypto from "crypto";
import * as request from "request-promise";
import { getGithubBearer } from "../helpers/github-auth.helper";
import { githubApiService } from "./github-api.service";

export enum githubWebhookEvents {
    ISSUES = "issues",
    PROJECT_COLUMN = "project_column"
}

export const githubWebhookService = {
    async getRepositoryWebhooks(
        data: {
            owner_name_or_userlogin: string;
            repository_name: string;
        },
        ctx: AuthContext,
        gotTokenAlready?: string
    ): Promise<any[]> {
        return request
            .get({
                url: `https://api.github.com/repos/${data.owner_name_or_userlogin}/${data.repository_name}/hooks`,
                auth: await getGithubBearer(ctx, gotTokenAlready),
                qs: {},
                json: true,
                headers: { "user-agent": "node.js" }
            })
            .then(res => {
                if (String(res.statusCode) === "404") {
                    return [];
                }
                return res;
            })
            .catch(err => {
                if (String(err.statusCode) === "404") {
                    return [];
                }
            });
    },

    async registerWebhook(
        ownerName: string | null,
        repositoryName: string,
        ctx: AuthContext
    ) {
        logger.info("githubWebhookService.registerWebhook()");
        const user = await githubApiService.getUser(ctx);
        const isOwner =
            ownerName !== undefined && ownerName !== "" && ownerName !== null;
        const isWebhookAlreadyRegistered = await githubWebhookService.isWebhookAlreadyBound(
            isOwner ? ownerName : user.login,
            repositoryName,
            `${process.env.INTEGRATIONS_API_URL}/devtools/github/sync/issue?project_id=${ctx.project_id}`,
            ctx
        );

        if (isWebhookAlreadyRegistered === true) {
            logger.info(
                `githubWebhookService.registerWebhook() : hook already bound to issue's repository '${ownerName}/${repositoryName}'`
            );
            return "already_registered";
        }
        const token = await getGithubBearer(ctx);

        logger.info(
            "githubWebhookService.registerWebhook() : getGithubBearer() : " +
                token
        );

        const requestOptions = {
            url: `https://api.github.com/repos/${
                ownerName !== null ? ownerName : user.login
            }/${repositoryName}/hooks`,
            auth: token,
            body: {
                name: "web",
                active: true,
                events: [
                    githubWebhookEvents.ISSUES,
                    githubWebhookEvents.PROJECT_COLUMN
                ],
                config: {
                    url: `${process.env.INTEGRATIONS_API_URL}/devtools/github/sync/issue?project_id=${ctx.project_id}`,
                    content_type: "json",
                    insecure_ssl: "0",
                    secret: process.env.GITHUB_WEBHOOK_SIGNATURE
                }
            },
            json: true,
            headers: { "user-agent": "node.js" }
        };

        logger.info(
            "githubWebhookService.registerWebhook() : POST options : " +
                JSON.stringify(requestOptions)
        );
        logger.info("githubWebhookService.registerWebhook() : reqt.post");
        return request
            .post(requestOptions)
            .then(webhookCreated => webhookCreated.id)
            .catch(error => {
                if (String(error.statusCode) === "404") {
                    console.log(
                        "Hook endpoint or repository does not exist for : ",
                        `https://api.github.com/repos/${
                            ownerName !== null ? ownerName : user.login
                        }/${repositoryName}/hooks`
                    );
                }
                return Promise.resolve({ statusCode: error.statusCode });
            });
    },

    async registerAllWebhooks(where: { project_id: string }) {
        const reposWithWebhookRegisteredOn = new Set<string>();
        const issuesWebhookLess = await githubStorageService.getIssuesOfProjectWithoutWebhook(
            where
        );
        await parallelPromiseHelper.uploadData(
            issuesWebhookLess || [],
            async issuesChunk => {
                return Promise.all(
                    issuesChunk.reduce((promises: any[], issue) => {
                        if (
                            !reposWithWebhookRegisteredOn.has(
                                issue.githubRepositoryName as string
                            )
                        ) {
                            promises.push(
                                githubWebhookService
                                    .registerWebhook(
                                        issue.githubOwnerName as string,
                                        issue.githubRepositoryName as string,
                                        {
                                            type: "INTEGRATION_JIRA",
                                            project_id: where.project_id
                                        }
                                    )
                                    .catch(e => {
                                        return null;
                                    })
                            );
                            reposWithWebhookRegisteredOn.add(
                                issue.githubOwnerName as string
                            );
                        }

                        return promises;
                    }, [])
                );
            },
            10
        );
    },

    async unregisterAllWebhooks(ctx: AuthContext, gotTokenAlready?: string) {
        console.log("github.unregisterAllWebhooks()");
        // github exposes a API endpoint that provides all currently registered webhooks.
        // This helps us to not bypass any webhook due to a issue with webhookID reference broken.
        const _unregisterAllWebhooksForAccountType = async (
            accountType: "org" | "user"
        ) => {
            let webhooksToUnregister: any[] = [];

            let accounts: any[] = [];
            if (accountType === "org") {
                accounts = await githubApiService.getOrganizations(
                    ctx,
                    gotTokenAlready
                );
            } else if (accountType === "user") {
                accounts = [
                    await githubApiService.getUser(ctx, gotTokenAlready)
                ];
            }
            for (const account of accounts) {
                const allRepositories = await githubApiService.getRepositories(
                    {
                        [accountType === "org"
                            ? "organization_name"
                            : "user_name"]: account.login
                    },
                    ctx,
                    gotTokenAlready
                );

                for (const repo of allRepositories) {
                    const repoWebhooks = await githubWebhookService.getRepositoryWebhooks(
                        {
                            owner_name_or_userlogin: account.login,
                            repository_name: repo.name
                        },
                        ctx,
                        gotTokenAlready
                    );

                    webhooksToUnregister = [
                        ...webhooksToUnregister,
                        ...repoWebhooks
                            .filter(
                                webhook =>
                                    webhook?.type?.toLowerCase() ===
                                        "repository" &&
                                    webhook?.config?.url?.includes(
                                        process.env.INTEGRATIONS_API_URL
                                    )
                            )
                            .map(whk => ({
                                webhookId: whk.id,
                                accountLogin: account.login,
                                repoName: repo.name
                            }))
                    ];
                }
            }

            webhooksToUnregister.length > 0 &&
                (await parallelPromiseHelper.uploadData(
                    webhooksToUnregister,
                    async webhooksChunk =>
                        Promise.all(
                            webhooksChunk.map(webhookData => {
                                githubWebhookService.unregisterWebhookOnRepo(
                                    {
                                        owner_or_user_login:
                                            webhookData.accountLogin,
                                        repoName: webhookData.repoName,
                                        webhookId: webhookData.webhookId
                                    },
                                    ctx,
                                    gotTokenAlready
                                );
                            })
                        ),
                    10
                ));
        };

        await _unregisterAllWebhooksForAccountType("org");
        await _unregisterAllWebhooksForAccountType("user");
        return Promise.resolve();
    },

    async unregisterWebhookOnRepo(
        where: {
            owner_or_user_login: string;
            repoName: string;
            webhookId: number;
        },
        ctx: AuthContext,
        gotTokenAlready?: string
    ) {
        const token = await getGithubBearer(ctx, gotTokenAlready);
        return request.delete({
            url: `https://api.github.com/repos/${where.owner_or_user_login}/${where.repoName}/hooks/${where.webhookId}`,
            auth: token,
            headers: { "user-agent": "node.js" }
        });
    },

    isWebhookSignatureValid(event): boolean {
        if (
            event.body === undefined ||
            event.body === null ||
            (typeof event.body === "object" &&
                (event.body as any).constructor.name === "object")
        ) {
            return false;
        }
        const hmac = crypto.createHmac(
            "sha1",
            (<any>process).env.GITHUB_WEBHOOK_SIGNATURE
        );
        hmac.update(event.body as string, "utf8");
        return (
            event.headers["X-Hub-Signature"] === `sha1=${hmac.digest("hex")}`
        );
    },

    async isWebhookAlreadyBound(
        owner_name_or_userlogin: string,
        repository_name: string,
        url: string,
        ctx: AuthContext
    ): Promise<boolean> {
        const repoWebhooks = await githubWebhookService.getRepositoryWebhooks(
            {
                owner_name_or_userlogin,
                repository_name
            },
            ctx
        );
        if (repoWebhooks.length === 0) {
            return false;
        }
        const foundWebhook = (repoWebhooks || []).find(
            (wh: any) =>
                (wh?.config?.url || "").includes(url) &&
                (wh?.active || false) === true &&
                (wh?.events || []).includes(githubWebhookEvents.ISSUES)
        );
        return foundWebhook !== undefined;
    }
};
